/**
 * Calendar-day helpers.
 *
 * A booking date is a calendar day, not an instant. `Date.toISOString()` shifts
 * to UTC first, so a day picked at local midnight in Kampala (UTC+3) serialises
 * as the *previous* day — every stay booked an evening early. Everything here
 * reads and writes the local calendar fields instead, so the day a guest taps
 * is the day the API receives.
 */

const MS_PER_DAY = 86_400_000;

/** Serialise a Date to `YYYY-MM-DD` using its local calendar fields. */
export function toISODate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Parse `YYYY-MM-DD` into a Date at local midnight. Invalid input yields null. */
export function fromISODate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));

  // Reject impossible dates that the constructor would silently roll over
  // (e.g. 2026-02-31 becoming 3 March).
  return date.getMonth() === Number(month) - 1 ? date : null;
}

/** Today at local midnight — the earliest selectable day. */
export function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** A copy of `date` moved by `days`, never mutating the input. */
export function addDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Whole nights between two days.
 *
 * Both ends are normalised to local midnight first, so a daylight-saving
 * boundary inside the range cannot round the count to the wrong integer.
 */
export function nightsBetween(from: Date | null, to: Date | null): number {
  if (!from || !to) return 0;
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  if (end <= start) return 0;
  return Math.round((end - start) / MS_PER_DAY);
}

/** `Mar 4` — the compact form used on chips and capsule segments. */
export function formatShortDay(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** `Wed, Mar 4` — the form used where a weekday helps the guest orient. */
export function formatWeekdayDay(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** `Mar 4 – 8` or `Mar 30 – Apr 2`, collapsing the month when it repeats. */
export function formatDayRange(from: Date | null, to: Date | null): string | null {
  if (!from) return null;
  if (!to) return formatShortDay(from);

  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();

  return sameMonth
    ? `${formatShortDay(from)} – ${to.getDate()}`
    : `${formatShortDay(from)} – ${formatShortDay(to)}`;
}

/** Every day from `start` to `end` inclusive, as fresh Date objects. */
export function eachDayInclusive(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (cursor <= last) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }

  return days;
}
